import React, { lazy } from "react";

// The one route table. Each entry owns its url, its i18n keys and its page component, so
// the app's labels (`useRoutes`) and the router's <Switch> (`PAGE_ROUTES`) cannot drift.
//
// One chunk per page. The lazy() specifiers point at concrete modules rather than the
// feature barrels (`../pages/users`) on purpose — a barrel specifier would pull every page
// of that feature, plus its shared form, into a single chunk.

// `lazy()` wants a module whose default export is a `ComponentType`. Several pages return
// `false | Element` or declare required props, so their signatures don't fit. This is the
// one place that mismatch is absorbed — the alternative was @ts-nocheck on the whole file.
const lazyPage = (load: () => Promise<any>) =>
  lazy(load as () => Promise<{ default: React.ComponentType }>);

// Rendered by Pages.tsx outside the authenticated <Switch>, so neither is a ROUTES entry.
export const Login = lazyPage(() => import("../pages/Login"));
export const PageNotFound = lazyPage(() => import("../pages/PageNotFound"));

const Dashboard = lazyPage(() => import("../pages/Dashboard"));

const UserList = lazyPage(() => import("../pages/users/UserList"));
// One component, one chunk, for both /users/create and /users/edit — the form page reads the
// route param to tell the two apart.
const UserFormPage = lazyPage(() => import("../pages/users/UserFormPage"));

const RoleList = lazyPage(() => import("../pages/roles/RoleList"));
const RoleCreate = lazyPage(() => import("../pages/roles/RoleCreate"));
const RoleEdit = lazyPage(() => import("../pages/roles/RoleEdit"));

const BrandList = lazyPage(() => import("../pages/brands/BrandList"));
const BrandCreate = lazyPage(() => import("../pages/brands/BrandCreate"));
const BrandEdit = lazyPage(() => import("../pages/brands/BrandEdit"));
const BrandDetail = lazyPage(() => import("../pages/brands/BrandDetail"));

const FeedList = lazyPage(() => import("../pages/feeds/FeedList"));
const FeedEdit = lazyPage(() => import("../pages/feeds/FeedEdit"));
const FeedDetail = lazyPage(() => import("../pages/feeds/FeedDetail"));

const InstrumentList = lazyPage(() => import("../pages/instruments/InstrumentList"));
const InstrumentEdit = lazyPage(() => import("../pages/instruments/InstrumentEdit"));
const InstrumentDetail = lazyPage(() => import("../pages/instruments/InstrumentDetail"));

const CategoryList = lazyPage(() => import("../pages/categories/CategoryList"));
const CategoryFormPage = lazyPage(() => import("../pages/categories/CategoryFormPage"));
const CategoryDetail = lazyPage(() => import("../pages/categories/CategoryDetail"));

const Exchanges = lazyPage(() => import("../pages/exchanges/Exchanges"));
const ExchangeCreate = lazyPage(() => import("../pages/exchanges/ExchangeCreate"));
const ExchangeEdit = lazyPage(() => import("../pages/exchanges/ExchangeEdit"));
const ExchangeDetail = lazyPage(() => import("../pages/exchanges/ExchangeDetail"));

const HolidayCreate = lazyPage(() => import("../pages/exchanges/HolidayCreate"));
const HolidayEdit = lazyPage(() => import("../pages/exchanges/HolidayEdit"));
const HolidayDetail = lazyPage(() => import("../pages/exchanges/HolidayDetail"));

// DualCharts is a named export, so it needs remapping onto `default` for lazy().
const DualCharts = lazyPage(() =>
  import("../pages/dualCharts/DualCharts").then(module => ({ default: module.DualCharts }))
);

const Suspended = lazyPage(() => import("../pages/suspended"));

export type RouteAction = {
  // The key the action is exposed under: routes.users.edit
  key: "create" | "edit" | "detail";
  // Absent for an action that opens on the parent page — see `query`.
  url?: string;
  labelKey: string;
  // Appended to `url` for the <Route> path only, so routes.users.edit.url stays clean.
  params?: string;
  exact?: boolean;
  Page?: React.ComponentType;
  // Search params the action opens with, in place of a url of its own.
  query?: Record<string, unknown>;
};

export type RouteConfig = {
  // The key the route is exposed under: routes.users. Not always the url segment —
  // dualCharts lives at /d3-charts.
  key: string;
  // Absent on a namespace that only groups actions: `holidays` has no page of its own,
  // and exposing a url for one would put a dead crumb in the header.
  url?: string;
  labelKey?: string;
  singularKey?: string;
  exact?: boolean;
  Page?: React.ComponentType;
  actions?: RouteAction[];
};

export type PageRoute = {
  path: string;
  exact?: boolean;
  Page: React.ComponentType;
};

// Entry order is the order `routes` exposes its keys in, which useCurrentPageDetails
// walks first-match-wins — keep the specific ahead of the general.
export const ROUTES: RouteConfig[] = [
  {
    key: "root",
    url: "/",
    labelKey: "PAGE_NAMES.DASHBOARD",
    exact: true,
    Page: Dashboard,
  },
  {
    key: "login",
    url: "/login",
    labelKey: "PAGE_NAMES.LOGIN",
    singularKey: "PAGE_NAMES.LOGIN",
  },
  // The four entries below carry no `Page`: nothing renders them yet, they exist for
  // their labels and for the urls other code builds from.
  {
    key: "businessaccounts",
    url: "/businessaccounts",
    labelKey: "PAGE_NAMES.BUSINESS_ACCOUNTS",
    singularKey: "BUSINESS_ACCOUNT.NAME",
    actions: [
      { key: "create", url: "/businessaccounts/create", labelKey: "BUSINESS_ACCOUNT.CREATE" },
      { key: "edit", url: "/businessaccounts/edit", labelKey: "BUSINESS_ACCOUNT.EDIT" },
      { key: "detail", url: "/businessaccounts/detail", labelKey: "BUSINESS_ACCOUNT.VIEW" },
    ],
  },
  {
    key: "permissions",
    url: "/permissions",
    labelKey: "PAGE_NAMES.PERMISSIONS",
    singularKey: "PERMISSION.NAME",
    actions: [
      { key: "create", url: "/permissions/create", labelKey: "PERMISSION.CREATE" },
      { key: "edit", url: "/permissions/edit", labelKey: "PERMISSION.EDIT" },
    ],
  },
  {
    key: "dealingportal",
    url: "/dealingportal",
    labelKey: "PAGE_NAMES.DEALING_PORTAL",
    actions: [{ key: "create", labelKey: "POSITION_ORDER.CREATE", query: { openPosition: true } }],
  },
  {
    key: "atpcnc",
    url: "/atpcnc",
    labelKey: "PAGE_NAMES.ATPCNC",
  },
  {
    key: "users",
    url: "/users",
    labelKey: "PAGE_NAMES.USERS",
    singularKey: "USER.NAME",
    exact: true,
    Page: UserList,
    actions: [
      {
        key: "edit",
        url: "/users/edit",
        labelKey: "USER.EDIT",
        params: ":userId",
        Page: UserFormPage,
      },
      { key: "create", url: "/users/create", labelKey: "USER.CREATE", Page: UserFormPage },
    ],
  },
  {
    key: "roles",
    url: "/roles",
    labelKey: "PAGE_NAMES.ROLES",
    singularKey: "ROLE.NAME",
    exact: true,
    Page: RoleList,
    actions: [
      { key: "edit", url: "/roles/edit", labelKey: "ROLE.EDIT", params: ":roleId", Page: RoleEdit },
      { key: "create", url: "/roles/create", labelKey: "ROLE.CREATE", Page: RoleCreate },
    ],
  },
  {
    key: "brands",
    url: "/brands",
    labelKey: "PAGE_NAMES.BRANDS",
    singularKey: "BRAND.NAME",
    exact: true,
    Page: BrandList,
    actions: [
      {
        key: "edit",
        url: "/brands/edit",
        labelKey: "BRAND.EDIT",
        params: ":brandId",
        Page: BrandEdit,
      },
      {
        key: "detail",
        url: "/brands/detail",
        labelKey: "BRAND.VIEW",
        params: ":brandId",
        Page: BrandDetail,
      },
      { key: "create", url: "/brands/create", labelKey: "BRAND.CREATE", Page: BrandCreate },
    ],
  },
  {
    key: "feeds",
    url: "/feeds",
    labelKey: "PAGE_NAMES.FEEDS",
    singularKey: "FEED.NAME",
    exact: true,
    Page: FeedList,
    actions: [
      { key: "edit", url: "/feeds/edit", labelKey: "FEED.EDIT", params: ":feedId", Page: FeedEdit },
      {
        key: "detail",
        url: "/feeds/detail",
        labelKey: "FEED.VIEW",
        params: ":feedId",
        Page: FeedDetail,
      },
    ],
  },
  {
    key: "instruments",
    url: "/instruments",
    labelKey: "PAGE_NAMES.INSTRUMENTS",
    singularKey: "INSTRUMENT.NAME",
    exact: true,
    Page: InstrumentList,
    actions: [
      {
        key: "edit",
        url: "/instruments/edit",
        labelKey: "INSTRUMENT.EDIT",
        params: ":instrumentId",
        Page: InstrumentEdit,
      },
      {
        key: "detail",
        url: "/instruments/detail",
        labelKey: "INSTRUMENT.VIEW",
        params: ":instrumentId",
        Page: InstrumentDetail,
      },
    ],
  },
  {
    key: "categories",
    url: "/categories",
    labelKey: "PAGE_NAMES.CATEGORIES",
    singularKey: "CATEGORY.NAME",
    exact: true,
    Page: CategoryList,
    actions: [
      {
        key: "edit",
        url: "/categories/edit",
        labelKey: "CATEGORY.EDIT",
        params: ":categoryId",
        Page: CategoryFormPage,
      },
      {
        key: "detail",
        url: "/categories/detail",
        labelKey: "CATEGORY.VIEW",
        params: ":categoryId",
        Page: CategoryDetail,
      },
      {
        key: "create",
        url: "/categories/create",
        labelKey: "CATEGORY.CREATE",
        Page: CategoryFormPage,
      },
    ],
  },
  {
    key: "exchanges",
    url: "/exchanges",
    labelKey: "PAGE_NAMES.EXCHANGES",
    singularKey: "EXCHANGE.NAME",
    exact: true,
    Page: Exchanges,
    actions: [
      {
        key: "edit",
        url: "/exchanges/edit",
        labelKey: "EXCHANGE.EDIT",
        params: ":exchangeId",
        Page: ExchangeEdit,
      },
      {
        key: "detail",
        url: "/exchanges/detail",
        labelKey: "EXCHANGE.VIEW",
        params: ":exchangeId",
        Page: ExchangeDetail,
      },
      {
        key: "create",
        url: "/exchanges/create",
        labelKey: "EXCHANGE.CREATE",
        Page: ExchangeCreate,
      },
    ],
  },
  {
    // A holiday belongs to an exchange, so every action takes both ids and /holidays
    // itself has no page of its own.
    key: "holidays",
    url: "/holidays",
    labelKey: "PAGE_NAMES.HOLIDAYS",
    singularKey: "HOLIDAYS.NAME",
    actions: [
      {
        key: "edit",
        url: "/holidays/edit",
        labelKey: "HOLIDAYS.EDIT",
        params: ":exchangeId/:holidayId",
        Page: HolidayEdit,
      },
      {
        key: "detail",
        url: "/holidays/detail",
        labelKey: "HOLIDAYS.VIEW",
        params: ":exchangeId/:holidayId",
        Page: HolidayDetail,
      },
      { key: "create", url: "/holidays/create", labelKey: "HOLIDAYS.CREATE", Page: HolidayCreate },
    ],
  },
  {
    key: "dualCharts",
    url: "/d3-charts",
    labelKey: "PAGE_NAMES.DUAL_CHARTS",
    singularKey: "PAGE_NAMES.DUAL_CHARTS",
    exact: true,
    Page: DualCharts,
  },
  {
    key: "suspended",
    url: "/suspended",
    labelKey: "PAGE_NAMES.SUSPENDED",
    singularKey: "PAGE_NAMES.SUSPENDED",
    exact: true,
    Page: Suspended,
  },
];

// The authenticated pages, in <Switch> order: only the first match renders, so an entity's
// parameterised actions come before the exact list route they sit under.
export const PAGE_ROUTES: PageRoute[] = ROUTES.flatMap(entity => {
  const pages: PageRoute[] = [];

  (entity.actions || []).forEach(({ url, params, exact, Page }) => {
    if (!Page || !url) return;
    pages.push({ path: params ? `${url}/${params}` : url, exact, Page });
  });

  if (entity.Page && entity.url) {
    pages.push({ path: entity.url, exact: entity.exact, Page: entity.Page });
  }

  return pages;
});
